import { Span } from '@pandiafi/design-system'
import React from 'react'
import { useTranslation } from 'react-i18next'

import { TRemittance } from '../../../../common/validations/remittance'
import { PaymentsByMonth } from '../../../../common/generated/residentApiSchemas'
import { toShortDate, localizeCurrency } from '../utils'
import { Divider } from '../Divider'
import { GridElement } from '../GridElement'
import { GridTable } from '../GridTable'
import { TableTotal } from './TableTotal'
import { MonthTotal } from './MonthTotal'

interface RemittanceTableProps {
  allRemittances: TRemittance[]
  paymentOrders: PaymentsByMonth['paymentOrders']
  withholdings: PaymentsByMonth['withholdingsUsed']
  year: string
  monthNumber: string
  paymentsSums: string[]
}

export const RemittanceTable: React.FC<RemittanceTableProps> = ({
  allRemittances,
  paymentOrders,
  withholdings,
  year,
  monthNumber,
  paymentsSums,
}) => {
  const { t } = useTranslation(['payments'])

  const filterISODateStringByMonth = (
    dateString: string,
    selectedYear: string,
    selectedMonth: string // can be single char
  ): boolean => {
    const [yyyy, mm] = dateString.split('-')
    return yyyy === selectedYear && mm === selectedMonth.padStart(2, '0')
  }

  const remittances = allRemittances.filter((r) =>
    filterISODateStringByMonth(r.paidDate, year, monthNumber)
  )

  const monthWithholdings = withholdings.filter((withholding) =>
    filterISODateStringByMonth(withholding.date, year, monthNumber)
  )

  const refundLabel = t('payments:refund')
  const transferLabel = t('payments:transfer')

  const withheldFromLabel = (
    withheldFrom: PaymentsByMonth['withholdingsUsed'][number]['withheldFrom']
  ) => {
    switch (withheldFrom) {
      case 'securityDeposit': {
        return t('payments:withheldFrom.securityDeposit')
      }
      case 'depositVoucher': {
        return t('payments:withheldFrom.depositVoucher')
      }
      case 'rooFee': {
        return t('payments:withheldFrom.rooFee')
      }
    }
  }

  const rows = [
    ...remittances.map((remittance, index) => ({
      key: `remittance-${index}-${remittance.paidDate}-${remittance.nameOfPayer}`,
      title:
        remittance.entryType === 'transfer'
          ? transferLabel
          : remittance.nameOfPayer,
      date: remittance.paidDate,
      amount: Number(remittance.amount),
    })),
    ...paymentOrders.map((paymentOrder, index) => ({
      key: `refund-${paymentOrder.executionDate}-${paymentOrder.amount}-${index}`,
      title: refundLabel,
      date: paymentOrder.executionDate,
      amount: -Number(paymentOrder.amount),
    })),
    ...monthWithholdings.map((withholding, index) => ({
      key: `withholding-${withholding.date}-${withholding.amount}-${index}`,
      title: withheldFromLabel(withholding.withheldFrom),
      date: withholding.date,
      amount: Number(withholding.amount),
    })),
  ].sort((a, b) => b.date.localeCompare(a.date))

  const amounts = rows.map((row) => row.amount.toFixed(2))

  return (
    <>
      {rows.length === 0 ? (
        <Span>{t('payments:noRemittances')}</Span>
      ) : (
        <>
          <GridTable>
            <GridElement columns={2}>
              <Span bold>{t('payments:payer')}</Span>
            </GridElement>

            <GridElement align="right">
              <Span bold>{t('payments:paidDate')}</Span>
            </GridElement>

            <GridElement align="right">
              <Span bold>{t('payments:sum')}</Span>
            </GridElement>
          </GridTable>

          <Divider />

          <GridTable>
            {rows.map((row) => (
              <React.Fragment key={row.key}>
                <GridElement columns={2}>{row.title}</GridElement>
                <GridElement align="right">{toShortDate(row.date)}</GridElement>
                <GridElement align="right">
                  {localizeCurrency(row.amount)}
                </GridElement>
              </React.Fragment>
            ))}
          </GridTable>

          <TableTotal sums={amounts} />
        </>
      )}

      <MonthTotal payments={paymentsSums} remittances={amounts} />
    </>
  )
}
